import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Image, Plus, X, Euro } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CreateListing = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'motorcycle',
    price: '',
    currency: 'EUR',
    condition: 'good',
    location: '',
    country: 'FR',
    images: [],
  });

  const categories = [
    { value: 'motorcycle', label: t('marketplace.categories.motorcycle') },
    { value: 'parts', label: t('marketplace.categories.parts') },
    { value: 'accessories', label: t('marketplace.categories.accessories') },
    { value: 'gear', label: t('marketplace.categories.gear') },
  ];

  const conditions = [
    { value: 'new', label: t('marketplace.conditions.new') },
    { value: 'like_new', label: t('marketplace.conditions.like_new') },
    { value: 'good', label: t('marketplace.conditions.good') },
    { value: 'fair', label: t('marketplace.conditions.fair') },
  ];

  const countries = [
    { value: 'FR', label: t('countries.FR') },
    { value: 'DE', label: t('countries.DE') },
    { value: 'ES', label: t('countries.ES') },
    { value: 'IT', label: t('countries.IT') },
    { value: 'BE', label: t('countries.BE') },
    { value: 'CH', label: t('countries.CH') },
  ];

  const addImage = () => {
    if (imageUrl && formData.images.length < 5) {
      setFormData({ ...formData, images: [...formData.images, imageUrl] });
      setImageUrl('');
    }
  };

  const removeImage = (index) => {
    setFormData({
      ...formData,
      images: formData.images.filter((_, i) => i !== index),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.price || parseFloat(formData.price) <= 0) {
      toast.error(i18n.language === 'fr' ? 'Veuillez entrer un prix valide' : 'Please enter a valid price');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price),
      };

      await axios.post(`${API_URL}/listings`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success(t('common.success'));
      navigate('/marketplace');
    } catch (error) {
      console.error('Error creating listing:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 md:px-8 py-8">
      {/* Back Button */}
      <button
        onClick={() => navigate('/marketplace')}
        className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
        data-testid="back-to-marketplace"
      >
        <ArrowLeft className="w-4 h-4" />
        {t('marketplace.title')}
      </button>

      <Card>
        <CardHeader>
          <CardTitle className="font-heading font-bold text-2xl uppercase tracking-tight">
            {t('marketplace.createListing')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">{i18n.language === 'fr' ? 'Titre' : 'Title'} *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder={i18n.language === 'fr' ? 'Honda CB500F 2020' : 'Honda CB500F 2020'}
                className="h-12"
                required
                data-testid="listing-title-input"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={i18n.language === 'fr' ? 'Décrivez votre annonce...' : 'Describe your listing...'}
                rows={4}
                required
                data-testid="listing-description-input"
              />
            </div>

            {/* Category & Condition */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{i18n.language === 'fr' ? 'Catégorie' : 'Category'} *</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger className="h-12" data-testid="listing-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{i18n.language === 'fr' ? 'État' : 'Condition'} *</Label>
                <Select 
                  value={formData.condition} 
                  onValueChange={(value) => setFormData({ ...formData, condition: value })}
                >
                  <SelectTrigger className="h-12" data-testid="listing-condition-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {conditions.map((cond) => (
                      <SelectItem key={cond.value} value={cond.value}>
                        {cond.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Price */}
            <div className="space-y-2">
              <Label htmlFor="price">{i18n.language === 'fr' ? 'Prix' : 'Price'} (EUR) *</Label>
              <div className="relative">
                <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="price"
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  placeholder="5000"
                  className="pl-10 h-12"
                  min="0"
                  step="0.01"
                  required
                  data-testid="listing-price-input"
                />
              </div>
            </div>

            {/* Location & Country */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="location">{i18n.language === 'fr' ? 'Ville' : 'City'} *</Label>
                <Input
                  id="location"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="Paris"
                  className="h-12"
                  required
                  data-testid="listing-location-input"
                />
              </div>

              <div className="space-y-2">
                <Label>{t('profile.country')} *</Label>
                <Select 
                  value={formData.country} 
                  onValueChange={(value) => setFormData({ ...formData, country: value })}
                >
                  <SelectTrigger className="h-12" data-testid="listing-country-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map((country) => (
                      <SelectItem key={country.value} value={country.value}>
                        {country.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Images */}
            <div className="space-y-4">
              <Label>
                <Image className="w-4 h-4 inline mr-1" />
                Images ({formData.images.length}/5)
              </Label>
              
              {/* Image preview */}
              {formData.images.length > 0 && (
                <div className="grid grid-cols-5 gap-2">
                  {formData.images.map((img, idx) => (
                    <div key={idx} className="relative aspect-square rounded-md overflow-hidden group">
                      <img src={img} alt={`Image ${idx + 1}`} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeImage(idx)}
                        className="absolute top-1 right-1 w-6 h-6 rounded-full bg-destructive text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`remove-image-${idx}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add image */}
              {formData.images.length < 5 && (
                <div className="flex gap-2">
                  <Input
                    type="url"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    placeholder="https://..."
                    className="h-12"
                    data-testid="image-url-input"
                  />
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={addImage}
                    disabled={!imageUrl}
                    data-testid="add-image-btn"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>

            {/* Submit */}
            <div className="flex gap-4 pt-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => navigate('/marketplace')}
                data-testid="cancel-create-listing"
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                className="flex-1 brand-glow font-bold uppercase tracking-wider"
                disabled={loading}
                data-testid="submit-create-listing"
              >
                {loading ? t('common.loading') : t('common.save')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreateListing;
